import { useEffect } from 'react';

import { SubscriptionType } from 'services/payment.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { isMac, isTouchDevice } from 'helpers/_helpers';
import { resetFiles } from 'components/app/_mediator/app-files.slice';
import { useLocalstore } from 'components/app/_mediator/localstore/use-localstore';
import { isNull } from 'components/app/_helpers/_app.helper';
import { setBeginnerMode, setIsTouchDevice, setOs, setStoryLoaded } from 'components/app/_mediator/app.slice';
import { resetFinalcuts, resetProjects, resetStories } from 'components/app/_mediator/app-data.slice';
import {
  setSelectedFile,
  setSelectedFinalcut,
  setSelectedProject,
  setSelectedStory,
} from 'components/app/projects/_mediator/projects.slice';
import { resetEditor } from 'components/app/editor/_mediator/editor.slice';
import { resetEditorControls } from 'components/app/editor/_mediator/editor-controls.slice';
import { resetBackup } from 'components/app/editor/_mediator/backup/backup.slice';
import { aggregator } from 'components/app/editor/_mediator/monitor/monitor-req.slice';
import { useMonitorReq } from 'components/app/editor/_mediator/monitor/use-monitor-req';
import { resetAudioTrack, setAudioId } from 'components/app/audio-track/_mediator/audio-track.slice';
import { resetSbPlayback } from 'components/app/storyboard/sb-player/_mediator/sb-player.slice';
import { useAppRestoreView } from './use-app-restore-view';
import { useAppRestoreActions } from './use-app-restore-actions';
import { useAppSseUpload } from './use-app-sse-upload';
import { useAppAvailable } from './use-app-available';
import { useAppFetcher } from './use-app-fetcher';
import { useAppRestore } from './use-app-restore';
import { useAppHotkeys } from './use-app-hotkeys';
import { useAppRedirect } from './use-app-redirect';
import { useAppUrls } from './use-app-urls';
import { useAppSse } from './use-app-sse';
import { useAppLogger } from '../use-app-logger';

export const useAppInit = () => {
  const subscriptionType = useAppSelector((state) => state.app__data.user?.subscriptionType);
  const uploadingFiles = useAppSelector((state) => state.app__filesUpload.uploadingFiles);
  const youtubeFiles = useAppSelector((state) => state.app__filesUpload.youtubeFiles);
  const selectedFolder = useAppSelector((state) => state.projects.selectedFolder);
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const dispatch = useAppDispatch();

  const errorBoundaryCb = useAppLogger();

  useAppUrls();
  useAppSse();
  useAppSseUpload();
  useAppHotkeys();
  useAppFetcher();
  useMonitorReq();
  useAppAvailable();

  /* local store */
  useAppRestore();
  useAppRestoreView();
  useAppRestoreActions();
  useAppRedirect();
  useLocalstore();

  useEffect(() => {
    dispatch(setOs(isMac()));
    dispatch(setIsTouchDevice(isTouchDevice()));
  }, []);

  useEffect(() => {
    dispatch(setBeginnerMode((subscriptionType ?? -1) < SubscriptionType.Hd));
  }, [subscriptionType]);

  useEffect(() => {
    const cb = (e: BeforeUnloadEvent) => {
      const hasUploadingFiles = uploadingFiles.length > 0;
      const hasYoutubeUploads = Object.keys(youtubeFiles).length > 0;
      if (hasUploadingFiles || hasYoutubeUploads) e.preventDefault();
    };
    window.addEventListener('beforeunload', cb);
    return () => window.removeEventListener('beforeunload', cb);
  }, [uploadingFiles.length, Object.keys(youtubeFiles).length]);

  /* reset */

  useEffect(() => {
    if (isNull(selectedFolder)) return;
    return () => {
      dispatch(setSelectedProject(null));
      dispatch(resetProjects());
    };
  }, [selectedFolder]);

  useEffect(() => {
    if (isNull(selectedProject)) return;
    return () => {
      dispatch(aggregator(null));
      dispatch(setSelectedStory(null));
      dispatch(setSelectedFile(null));
      dispatch(setAudioId(null));
      dispatch(resetStories());
      dispatch(resetFiles());
    };
  }, [selectedProject]);

  useEffect(() => {
    if (isNull(selectedStory)) return;
    return () => {
      dispatch(aggregator(selectedStory));
      dispatch(setSelectedFinalcut(null));
      dispatch(setStoryLoaded(false));
      dispatch(resetBackup());
      dispatch(resetSbPlayback());
      dispatch(resetAudioTrack());
      dispatch(resetEditorControls());
      dispatch(resetEditor());
      dispatch(resetFinalcuts());
    };
  }, [selectedStory]);

  return errorBoundaryCb;
};
